import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import { SeV2QuestionDto, useGetContractOriginCommunicationQuery } from "../../../../autogen/bff-api";
import { AnnouncementCard } from "../../../../common/components/communication/AnnouncementCard";
import { formatDateTime } from "../../../../common/components/communication/communicationUtils";
import { QuestionStatusChip } from "../../../../common/components/communication/QuestionStatusChip";
import { TipTap } from "../../../../common/input/TipTap/TipTap";

export const OriginCommunication = ({ contractId }: { contractId: string }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetContractOriginCommunicationQuery({ contractId });

  if (isLoading) {
    return (
      <Flex py="6" justifyContent="center">
        <Spinner />
      </Flex>
    );
  }

  const announcements = data?.announcements ?? [];
  const questions = data?.questions ?? [];
  if (announcements.length === 0 && questions.length === 0) return null;

  return (
    <Accordion allowToggle borderWidth="1px" borderColor="smBorder" borderRadius="md">
      <AccordionItem border="none">
        <AccordionButton px="4" py="3">
          <Box flex="1" textAlign="left" fontWeight="semibold">
            {t("From the sourcing event")}
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel px="4" pb="4">
          <Text fontSize="sm" color="smMuted" mb="4">
            {t("Communication from the sourcing event this contract originates from.")}
          </Text>

          {announcements.length > 0 && (
            <Flex direction="column" gap="3" mb={questions.length > 0 ? "6" : "0"}>
              <Text fontSize="sm" fontWeight="semibold" color="smText">
                {t("Published announcements")}
              </Text>
              {announcements.map((announcement) => (
                <AnnouncementCard key={announcement.id} announcement={announcement} />
              ))}
            </Flex>
          )}

          {questions.length > 0 && (
            <Flex direction="column" gap="3">
              <Text fontSize="sm" fontWeight="semibold" color="smText">
                {t("Questions and answers")}
              </Text>
              {questions.map((question) => (
                <OriginQuestionThread key={question.id} question={question} />
              ))}
            </Flex>
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

const OriginQuestionThread = ({ question }: { question: SeV2QuestionDto }) => {
  const { t } = useTranslation();
  return (
    <Box borderWidth="1px" borderColor="smBorder" rounded="lg" overflow="hidden">
      <HStack
        px="4"
        py="3"
        borderBottomWidth="1px"
        borderColor="smBorder"
        justify="space-between"
        align="center"
        spacing="3"
      >
        <Text fontSize="md" fontWeight="semibold" color="smText" minW={0} noOfLines={1}>
          {question.subject}
        </Text>
        <QuestionStatusChip status={question.status} />
      </HStack>

      <Box px="4" py="3">
        <Text fontSize="sm" fontWeight="semibold" color="smText" mb="1">
          {t("Question from")} {question.askedByOrgName}{" "}
          <Text as="span" fontWeight="normal" color="smMuted">
            · {formatDateTime(question.createdAt)}
          </Text>
        </Text>
        <TipTap content={question.content} editable={false} maxHeight="96" />
      </Box>

      {question.answer ? (
        <Box px="4" py="4" borderTopWidth="1px" borderColor="smBorder" bg="smPrimarySofter">
          <Text fontSize="sm" fontWeight="semibold" color="smChipTealFg" mb="1">
            {t("Answer from the buyer")}{" "}
            <Text as="span" fontWeight="normal" color="smMuted">
              · {formatDateTime(question.answer.answeredAt)}
            </Text>
          </Text>
          <TipTap content={question.answer.content} editable={false} maxHeight="96" />
        </Box>
      ) : (
        <HStack px="4" py="3" borderTopWidth="1px" borderColor="smBorder" spacing="2" color="smMuted">
          <Icon as={FiClock} boxSize="4" />
          <Text fontSize="sm">{t("Waiting for an answer from the buyer")}</Text>
        </HStack>
      )}
    </Box>
  );
};
